// Story 8.6 (ADO-2015), D-9 / AC2 — what the Shopper is told once the order is accepted: the
// order-sent page (Figma desktop 5464:87367, mobile and tablet 5464:87949), shown on /cart in
// place of the Order Review.
//
// It says what has happened, not what to wait for: the "Order sent" banner, with the delivery day
// the order was accepted for when there is one ("Your order has been sent for delivery on …") and
// a plain "sent successfully" when there is none (AC2: no delivery day is presented), then the
// order summary (the order reference, when it was sent, the depot with its depot icon, the
// delivery date, the order total) and the two ways on: View order history and Continue shopping.
// Everything but the depot icon comes from the server's answer, which is identical on a repeat
// send (AC1). The heading takes focus on mount, so a screen reader hears the outcome where the
// Submit button was.
//
// The order reference is the store's own reference when one was given, and ShopLink's order
// number (`ORD-<n>`) otherwise. There is no separate "Your reference" field (Figma 5490:26158).
//
// Story 8.4 (ADO-2013): where the sent cart's entry names its account (`namesAccount`: every
// Value Centre account) the depot is followed by the account's name, its number when the name is
// blank ("VC Dublin · Main grocery"), both from the server's answer.
//
// The depot icon is the depot's accent icon, the one the depot choice screen and the header
// switcher draw for it (`depotAccent`). The cart page passes the accent of the ordering entry the
// order was sent from; before the ordering choices load, it is read from the sent depot's code.
//
// One fact the frames do not draw is kept, in the frames' own style, because the story requires
// it: the products left out because they are no longer available (D-7). The order total excludes
// the deposit, and the page shows no deposit figure, as the frames draw none.
//
// Story 8.13 (ADO-2022), D-12: where the cart offered delivery or collection, the page says which
// (frames "Order Sent · Desktop 1440 · VC Dublin Main grocery · Delivery" and "· VC Dublin Tobacco
// · Collection"). A collection's lead names its day and time, and its fields are Fulfilment,
// Collection date (day · time) and Contact; a delivery's are Fulfilment and Delivery date. Special
// instructions follow either when given, their line breaks kept. Contact and Special instructions
// each start a new row in the left column. A cart offered no choice shows the page as before.

import { useEffect, useRef, type ReactNode } from 'react';
import { Link } from 'react-router';
import type { OrderConfirmation } from '../../api/orders.client';
import { Icon } from '../../design-system';
import { formatDeliveryDay, formatSentAt } from '../../shared/deliveryDayDisplay';
import { formatMoney } from '../../shared/money';
import { depotAccent, depotIconSrc, type DepotAccent } from '../Choose/depotAccent';
import linkButton from './LinkButton.module.css';
import styles from './OrderSentConfirmation.module.css';

export interface OrderSentConfirmationProps {
  readonly confirmation: OrderConfirmation;
  /** The accent of the ordering entry the order was sent from; absent while the ordering
   *  choices are unknown, when the sent depot's code decides it. */
  readonly accent?: DepotAccent | undefined;
  /** The sent cart's entry names its account (`namesItsAccount`); absent, the depot alone. */
  readonly namesAccount?: boolean | undefined;
}

function Field({
  label,
  testId,
  newRow = false,
  preserveLines = false,
  children,
}: {
  readonly label: string;
  readonly testId: string;
  /** Starts a new row in the left column, leaving the right one empty (8.13). */
  readonly newRow?: boolean;
  /** Keeps the value's line breaks (8.13's special instructions). */
  readonly preserveLines?: boolean;
  readonly children: ReactNode;
}) {
  return (
    <div className={styles.field} data-row={newRow ? 'new' : undefined}>
      <dt className={styles.label}>{label}</dt>
      <dd
        className={styles.value}
        data-testid={testId}
        data-preserve-lines={preserveLines ? 'true' : undefined}
      >
        {children}
      </dd>
    </div>
  );
}

export function OrderSentConfirmation({
  confirmation,
  accent,
  namesAccount = false,
}: OrderSentConfirmationProps) {
  const headingRef = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    headingRef.current?.focus();
  }, [confirmation.orderId]);

  const deliveryDay =
    confirmation.expectedDeliveryDate !== undefined
      ? formatDeliveryDay(confirmation.expectedDeliveryDate)
      : null;
  const collection =
    confirmation.fulfilmentMethod === 'COLLECTION' ? (confirmation.collection ?? null) : null;
  const collectionDay = collection !== null ? formatDeliveryDay(collection.date) : null;
  const depotMark = accent ?? depotAccent({ kind: 'DEPOT', depotCode: confirmation.depotCode });
  const sentAccount = confirmation.accountName.trim() || confirmation.accountCode;
  const depotLabel =
    namesAccount && sentAccount !== ''
      ? `${confirmation.depotName} · ${sentAccount}`
      : confirmation.depotName;

  return (
    // A plain div: the page's <main> is already named by this heading, so a section would add
    // a second landmark with the same name.
    <div className={styles.confirmation} data-testid="order-sent-confirmation">
      <div className={styles.banner}>
        <span className={styles.badge}>
          <Icon name="checkBold" size={18} className={styles.check} />
        </span>
        <div className={styles.bannerText}>
          <h1 id="order-sent-heading" className={styles.heading} tabIndex={-1} ref={headingRef}>
            Order sent
          </h1>
          <p className={styles.lead}>
            {collection !== null
              ? `Your order has been sent for collection on ${collectionDay} at ${collection.time}.`
              : deliveryDay !== null
                ? `Your order has been sent for delivery on ${deliveryDay}.`
                : 'Your order has been sent successfully.'}
          </p>
        </div>
      </div>

      <div className={styles.summary}>
        <dl className={styles.reference}>
          <dt className={styles.label}>Order reference</dt>
          <dd className={styles.referenceValue} data-testid="order-sent-reference">
            {confirmation.customerOrderReference ?? confirmation.orderReference}
          </dd>
        </dl>
        <hr className={styles.divider} />
        <dl className={styles.details}>
          <Field label="Submitted" testId="order-sent-submitted">
            <time dateTime={confirmation.submittedAt}>
              {formatSentAt(confirmation.submittedAt)}
            </time>
          </Field>
          <div className={styles.field}>
            <dt className={styles.label}>Depot</dt>
            <dd className={styles.depotValue}>
              <img
                src={depotIconSrc(depotMark)}
                width={16}
                height={16}
                alt=""
                className={styles.depotMark}
                data-testid="order-sent-depot-mark"
                data-accent={depotMark}
              />
              <span data-testid="order-sent-depot">{depotLabel}</span>
            </dd>
          </div>
          {confirmation.fulfilmentMethod !== undefined ? (
            <Field label="Fulfilment" testId="order-sent-fulfilment">
              {confirmation.fulfilmentMethod === 'COLLECTION' ? 'Collection' : 'Delivery'}
            </Field>
          ) : null}
          {collection !== null ? (
            <Field label="Collection date" testId="order-sent-collection-date">
              {`${collectionDay} · ${collection.time}`}
            </Field>
          ) : deliveryDay !== null ? (
            <Field label="Delivery date" testId="order-sent-delivery-day">
              {deliveryDay}
            </Field>
          ) : null}
          {collection !== null ? (
            <Field label="Contact" testId="order-sent-contact" newRow>
              {collection.contactName}
            </Field>
          ) : null}
          {confirmation.specialInstructions !== undefined ? (
            <Field
              label="Special instructions"
              testId="order-sent-special-instructions"
              newRow
              preserveLines
            >
              {confirmation.specialInstructions}
            </Field>
          ) : null}
        </dl>
        {confirmation.omittedLines.length > 0 ? (
          <div className={styles.omitted} data-testid="order-sent-omitted">
            <p className={styles.label}>Not ordered: no longer available</p>
            <ul className={styles.omittedList}>
              {/* One entry per omitted cart line, so a SKU can repeat (one line per section,
                  D-6a); the list is fixed once sent, so its position keeps each key unique. */}
              {confirmation.omittedLines.map((line, index) => (
                <li key={`${index}:${line.skuCode}`}>
                  {line.name !== '' ? `${line.name} (${line.skuCode})` : line.skuCode}
                </li>
              ))}
            </ul>
          </div>
        ) : null}
        <hr className={styles.divider} />
        <div className={styles.total}>
          <span className={styles.totalLabel}>Order total</span>
          <span className={styles.totalValue} data-testid="order-sent-total">
            {formatMoney(confirmation.total)}
          </span>
        </div>
      </div>

      <div className={styles.actions}>
        <Link
          to="/orders"
          className={`${linkButton.linkButton} ${linkButton.secondary} ${styles.action}`}
        >
          View order history
        </Link>
        <Link
          to="/products"
          className={`${linkButton.linkButton} ${linkButton.primary} ${styles.action}`}
        >
          Continue shopping
        </Link>
      </div>
    </div>
  );
}
